
import Vue from 'vue';
import { Runnit, RunnitNode, RunnitNodeRunResult } from '@run-diffusion/shared';
import { TemplateGenerationResultsMixin, AvailableResultItem, TEMPLATE_MAX_RESULTS_PER_NODE } from '@/mixins';
import RunnitResult from '@/views/Runnits/RunnitResult.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import { QueryDocumentSnapshot } from '@/firebase';

export default Vue.extend({
	name: 'AvailableGenerationsSection',
	mixins: [TemplateGenerationResultsMixin],
	components: {
		RunnitResult,
		BaseButton,
	},
	props: {
		selectedNode: {
			type: Object as () => RunnitNode | null,
			required: true,
		},
		sourceRunnit: {
			type: Object as () => Runnit | null,
			default: null,
		},
		selectedResults: {
			type: Array as () => Array<{
				templateNodeId: string
				sourceNodeRunId: string
				resultUuid: string
				result: RunnitNodeRunResult
			}>,
			default: () => [],
		},
	},
	data () {
		return {
			loading: false,
			loadingMore: false,
			availableResults: [] as AvailableResultItem[],
			canLoadMore: false,
			lastDoc: null as QueryDocumentSnapshot | null,
			isSourceRunnit: false,
			TEMPLATE_MAX_RESULTS_PER_NODE,
		};
	},
	computed: {
		assignedCount (): number {
			if (!this.selectedNode) return 0;
			return this.selectedResults.filter(r => r.templateNodeId === this.selectedNode.id).length;
		},
		canSelectMostRecentCount (): number {
			if (!this.selectedNode || this.assignedCount >= TEMPLATE_MAX_RESULTS_PER_NODE) return 0;
			const remainingSlots = TEMPLATE_MAX_RESULTS_PER_NODE - this.assignedCount;
			const unselectedResults = this.availableResults.filter(
				resultItem => !this.isSelected(resultItem.result.uuid)
			);
			return Math.min(remainingSlots, unselectedResults.length);
		},
	},
	watch: {
		selectedNode: {
			immediate: true,
			async handler (newVal: RunnitNode, oldVal: RunnitNode) {
				if (newVal != oldVal) {
					if (newVal) {
						await this.loadAvailableResults();
					} else {
						this.availableResults = [];
						this.canLoadMore = false;
						this.lastDoc = null;
					}
				}
			},
		},
	},
	methods: {
		async loadAvailableResults () {
			if (!this.selectedNode) return;

			this.loading = true;
			this.canLoadMore = false;
			this.lastDoc = null;
			try {
				const response = await this.loadAvailableResultsForNode(this.selectedNode, this.sourceRunnit, null);
				this.availableResults = response.results;
				this.canLoadMore = response.canLoadMore;
				this.lastDoc = response.lastDoc;
				this.isSourceRunnit = response.isSourceRunnit;
			} catch (error) {
				console.error('Error loading available results:', error);
			} finally {
				this.loading = false;
			}
		},
		async loadMoreAvailableResults () {
			if (!this.selectedNode || !this.canLoadMore || this.loadingMore || !this.lastDoc) return;

			this.loadingMore = true;
			try {
				const response = await this.loadAvailableResultsForNode(
					this.selectedNode,
					this.isSourceRunnit ? this.sourceRunnit : null,
					this.lastDoc
				);
				this.availableResults = [...this.availableResults, ...response.results];
				this.canLoadMore = response.canLoadMore;
				this.lastDoc = response.lastDoc;
				this.isSourceRunnit = response.isSourceRunnit;
			} catch (error) {
				console.error('Error loading more available results:', error);
			} finally {
				this.loadingMore = false;
			}
		},
		isSelected (resultUuid: string): boolean {
			if (!this.selectedNode) return false;
			return this.selectedResults.some(
				r => r.templateNodeId === this.selectedNode.id && r.resultUuid === resultUuid
			);
		},
		onSelect (resultItem: AvailableResultItem) {
			if (!this.selectedNode) return;

			// Check if already selected
			if (this.isSelected(resultItem.result.uuid)) {
				// Unselect
				this.$emit('result-unselected', resultItem.result.uuid, this.selectedNode.id);
				return;
			}

			// Check limit
			if (this.assignedCount >= TEMPLATE_MAX_RESULTS_PER_NODE) {
				return;
			}

			// Select
			this.$emit('result-selected', {
				templateNodeId: this.selectedNode.id,
				sourceNodeRunId: resultItem.nodeRunId,
				resultUuid: resultItem.result.uuid,
				result: resultItem.result,
			});
		},
		selectMostRecent () {
			if (!this.selectedNode || this.assignedCount >= TEMPLATE_MAX_RESULTS_PER_NODE) return;

			const remainingSlots = TEMPLATE_MAX_RESULTS_PER_NODE - this.assignedCount;

			// Get unselected results (availableResults is already sorted by initAt desc, so most recent first)
			const unselectedResults = this.availableResults.filter(
				resultItem => !this.isSelected(resultItem.result.uuid)
			);

			// Select the most recent ones up to the remaining slots
			const toSelect = unselectedResults.slice(0, remainingSlots);

			// Build array of all results to select
			const resultsToSelect = toSelect.map(resultItem => ({
				templateNodeId: this.selectedNode.id,
				sourceNodeRunId: resultItem.nodeRunId,
				resultUuid: resultItem.result.uuid,
				result: resultItem.result,
			}));

			// Emit bulk selection event with all results at once
			this.$emit('results-selected-bulk', resultsToSelect);
		},
	},
});
